import { supabase } from "@/shared/lib/supabaseClient";
import type { Database } from "@/shared/types/database.types";

/**
 * Camada de dados do elo Comunicados (migration 0099).
 *
 * Regras que este service NUNCA reimplementa (moram inteiras no banco):
 * - Visibilidade de leitura: RLS de `comunicados` (`pode_ver_comunicado`) —
 *   igreja toda, ministérios de destino, elo ativo/visível, prazo vencido. Este
 *   service nunca filtra por igreja/ministério/prazo no cliente.
 * - Autoria e destinos: `comunicado_autoria`/`comunicado_destinos` só são
 *   legíveis por quem gerencia o elo e pelo administrador. Para o membro as
 *   duas relações voltam vazias — por isso `detalhesVisiveis` (autoria
 *   presente) é o único sinal confiável de "esta pessoa pode ver os detalhes";
 *   `destinos` vazio NÃO quer dizer "igreja toda" para quem não vê detalhes.
 * - Escrita: nenhuma tabela aceita escrita direta; toda mudança passa pelas 5
 *   RPCs (publicar/editar/arquivar/restaurar/apagar), que revalidam permissão,
 *   igreja, versão (concorrência otimista) e prazo.
 * - Notificações: fora do escopo desta fase (F6).
 */
export type ComunicadoSituacao = "publicado" | "arquivado" | "apagado";

type ComunicadoRow = Database["public"]["Tables"]["comunicados"]["Row"];

export interface DestinoComunicado {
  ministerioId: string;
  ministerioNome: string;
}

export interface ComunicadoComRelacoes extends Omit<ComunicadoRow, "situacao"> {
  situacao: ComunicadoSituacao;
  /** Nome de quem publicou. `null` para quem não pode ver autoria (membro). */
  autorNome: string | null;
  /** `true` só quando o banco liberou autoria/destinos (gestão do elo ou administrador). */
  detalhesVisiveis: boolean;
  /** Ministérios de destino. Vazio + `detalhesVisiveis` = igreja toda. */
  destinos: DestinoComunicado[];
}

export interface PaginaComunicados {
  itens: ComunicadoComRelacoes[];
  total: number;
}

export interface FiltroListaComunicados {
  situacao: Exclude<ComunicadoSituacao, "apagado">;
  /** 1-based. */
  pagina: number;
}

export interface DadosComunicado {
  titulo: string;
  corpo: string;
  /** Vazio = igreja toda. */
  ministerioIds: string[];
  apagarAutomaticamente: boolean;
}

export interface PrazoComunicado {
  id: string;
  titulo: string;
  contagem_inicio_em: string;
}

export interface ResultadoRestauracao {
  versao: number;
  contagemReiniciada: boolean;
  /** ISO do novo vencimento, só quando a contagem de 30 dias recomeçou. */
  apagaEm: string | null;
}

/** Padrão do sistema: 10 registros por página. */
export const COMUNICADOS_POR_PAGINA = 10;

const SELECT_COMUNICADO =
  "*, comunicado_autoria(autor_nome), comunicado_destinos(ministerio_id, ministerios(nome))";

interface LinhaComunicado extends Omit<ComunicadoRow, "situacao"> {
  situacao: string;
  comunicado_autoria: { autor_nome: string } | { autor_nome: string }[] | null;
  comunicado_destinos: { ministerio_id: string; ministerios: { nome: string } | null }[] | null;
}

/**
 * Mensagens que as RPCs levantam já em PT-BR, prontas para a tela. Qualquer
 * outro texto (erro bruto do Postgres, rede) NUNCA vai para o usuário: cai no
 * `fallback` do chamador.
 */
const MENSAGENS_DAS_RPCS = new Set([
  "Comunicado não encontrado.",
  "Escolha no máximo 50 ministérios.",
  "Ministério inválido para esta igreja.",
  "O comunicado foi alterado por outra pessoa. Atualize a lista e tente de novo.",
  "O elo Comunicados está desativado para esta igreja.",
  "O texto deve ter de 1 a 1000 caracteres.",
  "O texto tem caracteres de controle não permitidos.",
  "O título deve ter de 1 a 100 caracteres.",
  "O título não pode ter quebra de linha nem caracteres de controle.",
]);

/** D7: a regra do prazo vencido começa com este trecho fixo. */
const INICIO_MENSAGEM_PRAZO = "O prazo de 30 dias deste comunicado já terminou.";

function traduzErroComunicado(error: { code?: string; message?: string } | null, fallback: string): Error {
  if (!error) return new Error(fallback);
  const mensagem = error.message ?? "";
  if (error.code === "40001") {
    return new Error("O comunicado foi alterado por outra pessoa. Atualize a lista e tente de novo.");
  }
  if (error.code === "42501" || /row-level security/i.test(mensagem)) {
    return new Error("Você não tem permissão para esta ação. Só quem lidera ou gerencia o elo Comunicados pode fazer isso.");
  }
  if (MENSAGENS_DAS_RPCS.has(mensagem) || mensagem.startsWith(INICIO_MENSAGEM_PRAZO)) {
    return new Error(mensagem);
  }
  return new Error(fallback);
}

function paraComunicado(linha: LinhaComunicado): ComunicadoComRelacoes {
  const autoria = Array.isArray(linha.comunicado_autoria) ? linha.comunicado_autoria[0] : linha.comunicado_autoria;
  const { comunicado_autoria: _autoria, comunicado_destinos: destinos, ...resto } = linha;
  return {
    ...resto,
    situacao: linha.situacao as ComunicadoSituacao,
    autorNome: autoria?.autor_nome ?? null,
    detalhesVisiveis: !!autoria,
    destinos: (destinos ?? [])
      .map((d) => ({ ministerioId: d.ministerio_id, ministerioNome: d.ministerios?.nome ?? "—" }))
      .sort((a, b) => a.ministerioNome.localeCompare(b.ministerioNome, "pt-BR")),
  };
}

async function listarPagina(
  situacao: FiltroListaComunicados["situacao"],
  de: number,
  ate: number,
): Promise<PaginaComunicados> {
  const { data, error, count } = await supabase
    .from("comunicados")
    .select(SELECT_COMUNICADO, { count: "exact" })
    .eq("situacao", situacao)
    .order("publicado_em", { ascending: false })
    .order("id", { ascending: true })
    .range(de, ate);
  if (error) {
    // Página além do fim (ex.: apagou o último item da última página): lista vazia,
    // a tela volta uma página sozinha.
    if (error.code === "PGRST103") return { itens: [], total: 0 };
    throw new Error("Não foi possível carregar os comunicados.");
  }
  return {
    itens: ((data ?? []) as unknown as LinhaComunicado[]).map(paraComunicado),
    total: count ?? 0,
  };
}

export const comunicadosService = {
  /** Uma página (10) de comunicados publicados ou arquivados, do mais recente ao mais antigo. */
  async listar({ situacao, pagina }: FiltroListaComunicados): Promise<PaginaComunicados> {
    const de = (Math.max(1, pagina) - 1) * COMUNICADOS_POR_PAGINA;
    return listarPagina(situacao, de, de + COMUNICADOS_POR_PAGINA - 1);
  },

  /** Os mais recentes publicados (preview da Início) e o total, para o "+N". */
  async listarRecentes(limite: number): Promise<PaginaComunicados> {
    return listarPagina("publicado", 0, Math.max(1, limite) - 1);
  },

  /** Um comunicado pelo id, ou `null` se a RLS não o devolver (inexistente e proibido são iguais). */
  async detalhe(id: string): Promise<ComunicadoComRelacoes | null> {
    const { data, error } = await supabase
      .from("comunicados")
      .select(SELECT_COMUNICADO)
      .eq("id", id)
      .maybeSingle();
    if (error) throw new Error("Não foi possível carregar o comunicado.");
    return data ? paraComunicado(data as unknown as LinhaComunicado) : null;
  },

  /**
   * Publicados marcados para "apagar sozinho" (id, título, início da contagem) —
   * base do aviso "será apagado amanhã" da gestão. Pequeno por natureza: o banco
   * já esconde os vencidos.
   */
  async listarPrazosPublicados(): Promise<PrazoComunicado[]> {
    const { data, error } = await supabase
      .from("comunicados")
      .select("id, titulo, contagem_inicio_em")
      .eq("situacao", "publicado")
      .eq("apagar_automaticamente", true);
    if (error) throw new Error("Não foi possível verificar os prazos dos comunicados.");
    return data ?? [];
  },

  /** Ministérios dos quais a pessoa participa (vínculo ativo) — para o rótulo "Só: …" da gestão. */
  async meusMinisteriosParticipo(pessoaId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from("pessoa_ministerio")
      .select("ministerio_id")
      .eq("pessoa_id", pessoaId)
      .eq("status", "ativo");
    if (error) throw new Error("Não foi possível carregar seus ministérios.");
    return [...new Set((data ?? []).map((v) => v.ministerio_id))];
  },

  async publicar(dados: DadosComunicado): Promise<string> {
    const { data, error } = await supabase.rpc("comunicado_publicar", {
      p_titulo: dados.titulo,
      p_corpo: dados.corpo,
      p_ministerio_ids: dados.ministerioIds.length ? dados.ministerioIds : undefined,
      p_apagar_automaticamente: dados.apagarAutomaticamente,
    });
    if (error || !data) throw traduzErroComunicado(error, "Não foi possível publicar o comunicado.");
    return data;
  },

  /** Devolve a nova versão. A contagem de 30 dias NÃO reinicia ao editar. */
  async editar(id: string, versao: number, dados: DadosComunicado): Promise<number> {
    const { data, error } = await supabase.rpc("comunicado_editar", {
      p_id: id,
      p_versao: versao,
      p_titulo: dados.titulo,
      p_corpo: dados.corpo,
      p_ministerio_ids: dados.ministerioIds.length ? dados.ministerioIds : undefined,
      p_apagar_automaticamente: dados.apagarAutomaticamente,
    });
    if (error || data === null) throw traduzErroComunicado(error, "Não foi possível salvar as alterações.");
    return data;
  },

  async arquivar(id: string, versao: number): Promise<number> {
    const { data, error } = await supabase.rpc("comunicado_arquivar", { p_id: id, p_versao: versao });
    if (error || data === null) throw traduzErroComunicado(error, "Não foi possível arquivar o comunicado.");
    return data;
  },

  async restaurar(id: string, versao: number): Promise<ResultadoRestauracao> {
    const { data, error } = await supabase.rpc("comunicado_restaurar", { p_id: id, p_versao: versao });
    if (error || !data) throw traduzErroComunicado(error, "Não foi possível restaurar o comunicado.");
    const r = data as { versao: number; contagem_reiniciada: boolean; apaga_em: string | null };
    return { versao: r.versao, contagemReiniciada: r.contagem_reiniciada, apagaEm: r.apaga_em };
  },

  /** Apaga para todos. O registro fica guardado 90 dias no banco, sem tela, e depois é eliminado. */
  async apagar(id: string, versao: number): Promise<void> {
    const { error } = await supabase.rpc("comunicado_apagar", { p_id: id, p_versao: versao });
    if (error) throw traduzErroComunicado(error, "Não foi possível apagar o comunicado.");
  },
};
